import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { EVENTO_ANCORA, abrirPorAncora, limparAncora } from './abrirPorAncora';

// Folga entre a barra fixa de âncoras e o item para o qual se rola.
const FOLGA_ROLAGEM = 8;

// Janela (ms) em que a mesma âncora, vinda por outro caminho, conta como o
// mesmo gesto: uma navegação pode disparar `hashchange` e a mudança do roteador
// juntos, e o item só deve rolar uma vez.
const JANELA_REPETICAO = 300;

// A âncora de um `#nsd`, sem o `#` e já decodificada. Um `%` malformado
// (`#%E0%A4%A`) não vale como âncora.
const ancoraDoHash = (hash) => {
  try {
    return decodeURIComponent(String(hash ?? '').replace(/^#/, ''));
  } catch (e) {
    return '';
  }
};

// A âncora no endereço do navegador.
const ancoraDoEndereco = () => ancoraDoHash(window.location.hash);

// O endereço do roteador. O bloco também é desenhado fora de um <Router> (nos
// testes, em prévias), onde `useLocation` estoura ao ler o contexto que não
// existe; ele já chamou `useContext` antes de estourar, então a ordem dos hooks
// é a mesma nos dois casos.
const useLocationSegura = () => {
  try {
    return useLocation();
  } catch (e) {
    return null;
  }
};

// Rola até o item descontando a barra de âncoras fixa (se houver) e a folga.
// No celular a barra deixa de ser fixa (position: static): aí não há o que
// descontar.
const rolarAte = (el) => {
  const barra = document.querySelector('.ag-anchor-nav--sticky');
  const barraFixa =
    barra && window.getComputedStyle(barra).position !== 'static';
  const alturaBarra = barraFixa ? barra.offsetHeight : 0;
  const top =
    el.getBoundingClientRect().top +
    window.pageYOffset -
    alturaBarra -
    FOLGA_ROLAGEM;
  const reduzir = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  )?.matches;
  window.scrollTo({
    top: Math.max(0, top),
    behavior: reduzir ? 'auto' : 'smooth',
  });
};

/**
 * AccordionView — suporta 3 formatos:
 *
 * 1) items[] novo: [{ id, question, blocks, blocks_layout }]  (editável no Volto)
 * 2) items[] simples: [{ question, answer }]                  (Q&A textarea — schema antigo)
 * 3) nested legado: data = { blocks, blocks_layout }          (accordion → migrate)
 *
 * Campos opcionais (sem eles o HTML é o de sempre):
 * - por item, `resumo` (linha sob o título, dentro do <summary>) e `ancora`
 *   (vira o `id` do <details>);
 * - no bloco, `abrirTodas` (botão "Abrir todas" / "Recolher todas").
 * Um `#ancora` no endereço, ao montar e a cada mudança (do navegador ou do
 * roteador: um link `#` no texto usa `history.pushState`, que não dispara
 * `hashchange`), ou o evento `agsus:ancora` (a barra de âncoras) abre o item e
 * rola até ele.
 */
const AccordionView = ({ data, metadata, properties }) => {
  const itemsArr = Array.isArray(data?.items) ? data.items : [];
  const newPanels = itemsArr.map((it, i) => ({
    id: it.id || `item-${i}`,
    title: it.question,
    blocks: it.blocks,
    blocks_layout: it.blocks_layout,
    answer: it.answer,
    resumo: typeof it.resumo === 'string' ? it.resumo.trim() : '',
    ancora: limparAncora(it.ancora),
  }));

  const nested = data?.data;
  const nestedPanels = nested?.blocks_layout?.items?.length
    ? nested.blocks_layout.items.map((pid) => ({
        id: pid,
        title: nested.blocks[pid]?.title,
        blocks: nested.blocks[pid]?.blocks,
        blocks_layout: nested.blocks[pid]?.blocks_layout,
      }))
    : [];

  const panels = newPanels.length > 0 ? newPanels : nestedPanels;

  const abrirTodas = !!data?.abrirTodas;
  const listaRef = useRef(null);
  const ultimaAncoraRef = useRef({ ancora: '', em: 0 });
  const location = useLocationSegura();
  const [todasAbertas, setTodasAbertas] = useState(false);

  // Os itens desta sanfona (e só dela: os de uma sanfona interna não contam).
  const itensDaLista = () =>
    Array.from(listaRef.current?.children || []).filter(
      (el) => el.tagName === 'DETAILS',
    );

  // O botão segue os `open` reais, que o leitor também muda clicando.
  const sincronizar = () => {
    const itens = itensDaLista();
    setTodasAbertas(itens.length > 0 && itens.every((d) => d.open));
  };

  const alternarTodas = () => {
    const itens = itensDaLista();
    const abrir = !itens.every((d) => d.open);
    itens.forEach((d) => {
      d.open = abrir;
    });
    sincronizar();
  };

  // Abre o item da âncora e rola até ele. Só a sanfona dona do item age:
  // com sanfonas dentro de sanfonas, todas ouvem o mesmo endereço, e
  // `abrirPorAncora` já abre os itens de fora. A mesma âncora de novo dentro da
  // janela (o `hashchange` e o roteador do mesmo clique) só abre, sem rolar.
  const irParaAncora = (id) => {
    const ancora = limparAncora(id);
    if (!ancora || !itensDaLista().some((d) => d.id === ancora)) return;
    const agora = Date.now();
    const ultima = ultimaAncoraRef.current;
    const repetida =
      ultima.ancora === ancora && agora - ultima.em < JANELA_REPETICAO;
    if (!repetida) ultimaAncoraRef.current = { ancora, em: agora };
    const item = abrirPorAncora(ancora);
    if (item && !repetida) rolarAte(item);
    sincronizar();
  };

  useEffect(() => {
    const aoMudarEndereco = () => irParaAncora(ancoraDoEndereco());
    const aoAvisarDaBarra = (e) => irParaAncora(e?.detail?.id);
    aoMudarEndereco();
    window.addEventListener('hashchange', aoMudarEndereco);
    window.addEventListener(EVENTO_ANCORA, aoAvisarDaBarra);
    return () => {
      window.removeEventListener('hashchange', aoMudarEndereco);
      window.removeEventListener(EVENTO_ANCORA, aoAvisarDaBarra);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // O roteador: um link `#` no texto (UniversalLink) navega com
  // `history.pushState`, sem `hashchange`. A chave entra para o mesmo link,
  // clicado de novo com o item já fechado, abrir outra vez.
  const hashDoRoteador = location?.hash || '';
  const chaveDoRoteador = location?.key;
  useEffect(() => {
    if (hashDoRoteador) irParaAncora(ancoraDoHash(hashDoRoteador));
  }, [hashDoRoteador, chaveDoRoteador]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sincronizar();
  }, [abrirTodas, panels.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (panels.length === 0) {
    return (
      <section className="ag-accordion">
        {data.title && <h2 className="ag-accordion__heading">{data.title}</h2>}
        <p className="ag-accordion__empty">Sem itens.</p>
      </section>
    );
  }

  const openFirst = !!data?.openFirst;

  return (
    <section className="ag-accordion">
      {data.eyebrow && (
        <span className="ag-accordion__eyebrow">{data.eyebrow}</span>
      )}
      {data.title && <h2 className="ag-accordion__heading">{data.title}</h2>}

      {abrirTodas && (
        <div className="ag-accordion__acoes">
          <button
            type="button"
            className="ag-accordion__abrir-todas"
            onClick={alternarTodas}
          >
            {todasAbertas ? 'Recolher todas' : 'Abrir todas'}
          </button>
        </div>
      )}

      <div className="ag-accordion__list" ref={listaRef}>
        {panels.map((p, i) => (
          <details
            key={p.id}
            id={p.ancora || undefined}
            className="accordion__item ag-accordion__item"
            open={openFirst && i === 0}
            onToggle={abrirTodas ? sincronizar : undefined}
          >
            <summary className="accordion__trigger ag-accordion__trigger">
              {p.resumo ? (
                <span className="ag-accordion__textos">
                  <span className="ag-accordion__titulo">
                    {p.title || `Item ${i + 1}`}
                  </span>
                  <span className="ag-accordion__resumo">{p.resumo}</span>
                </span>
              ) : (
                p.title || `Item ${i + 1}`
              )}
            </summary>
            <div className="accordion__panel ag-accordion__panel">
              {p.blocks && p.blocks_layout ? (
                <RenderBlocks
                  // A página onde o bloco está: sem isto, um bloco de dentro do
                  // item (a Galeria, por exemplo) não sabe o caminho. Mesmo
                  // padrão do Grid do core.
                  metadata={metadata || properties}
                  content={{
                    blocks: p.blocks,
                    blocks_layout: p.blocks_layout,
                  }}
                />
              ) : (
                p.answer && <p>{p.answer}</p>
              )}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
};

AccordionView.propTypes = {
  data: PropTypes.shape({
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    openFirst: PropTypes.bool,
    abrirTodas: PropTypes.bool,
    items: PropTypes.array,
    data: PropTypes.object,
  }).isRequired,
  metadata: PropTypes.object,
  properties: PropTypes.object,
};

export default AccordionView;
